import type { TimelineLayout } from '@/store/activity-store'

/**
 * Sizing rules for the resizable timeline (bottom) panel. Pure functions of
 * the viewport size and preferences, so the clamping is unit-testable
 * without layout.
 */

/** Smallest height (px) the panel can be dragged/keyed to. */
export const TIMELINE_PANEL_MIN_PX = 160
/** Largest height, as a fraction of the viewport height -- the workspace above keeps the rest. */
export const TIMELINE_PANEL_MAX_FRACTION = 0.7
/**
 * Cap for the *automatic* stacked-mode growth on narrow (below `md`)
 * viewports, where the activity list also stacks above the workspace: auto
 * growth there stops at half the viewport so the workspace isn't squeezed
 * out. The user can still drag up to `TIMELINE_PANEL_MAX_FRACTION`.
 */
export const TIMELINE_PANEL_AUTO_MAX_FRACTION_NARROW = 0.5
/** Tailwind's `md` breakpoint (px). */
export const MD_BREAKPOINT_PX = 768
/** Arrow-key step (px); Shift+Arrow moves `4x` this. */
export const TIMELINE_PANEL_KEY_STEP_PX = 16

export interface PanelBounds {
  min: number
  max: number
}

/** `[min, max]` panel height for a viewport height; `max` never below `min`. */
export function timelinePanelBounds(viewportHeight: number): PanelBounds {
  const max = Number.isFinite(viewportHeight)
    ? Math.floor(viewportHeight * TIMELINE_PANEL_MAX_FRACTION + 1e-6) // 700 * 0.7 = 489.999...
    : Infinity
  return { min: TIMELINE_PANEL_MIN_PX, max: Math.max(TIMELINE_PANEL_MIN_PX, max) }
}

/** The layout's default height: 256 px (Tailwind `h-64`) below `md`, 288 px (`h-72`) from `md` up. */
export function defaultTimelinePanelHeight(viewportWidth: number): number {
  return viewportWidth >= MD_BREAKPOINT_PX ? 288 : 256
}

export function clampTimelinePanelHeight(heightPx: number, bounds: PanelBounds): number {
  return Math.round(Math.min(bounds.max, Math.max(bounds.min, heightPx)))
}

export interface ResolveTimelinePanelHeightArgs {
  /** Persisted user choice (`ui.timelineHeightPx`); absent/invalid = automatic. */
  userHeightPx: number | undefined
  layout: TimelineLayout
  /**
   * Height the stacked chart asks for so each panel gets its preferred plot
   * height (`stackedChartHeightFor` plus the timeline's header rows and the
   * panel frame), or `null` when not stacked / not yet measured.
   */
  preferredStackedHeightPx: number | null
  viewportWidth: number
  viewportHeight: number
}

/**
 * The panel height actually applied. A user-set height always wins (just
 * re-clamped to the current viewport -- if it's too small for comfortable
 * stacked panels, the chart shrinks them to fit instead, never scrolls).
 * Otherwise the layout default, grown in stacked mode to fit every panel,
 * up to the viewport cap.
 */
export function resolveTimelinePanelHeight(args: ResolveTimelinePanelHeightArgs): number {
  const { userHeightPx, layout, preferredStackedHeightPx, viewportWidth, viewportHeight } = args
  const bounds = timelinePanelBounds(viewportHeight)
  if (userHeightPx != null && Number.isFinite(userHeightPx)) {
    return clampTimelinePanelHeight(userHeightPx, bounds)
  }
  const fallback = defaultTimelinePanelHeight(viewportWidth)
  if (layout !== 'stacked' || preferredStackedHeightPx == null) {
    return clampTimelinePanelHeight(fallback, bounds)
  }
  const autoCap =
    viewportWidth >= MD_BREAKPOINT_PX
      ? bounds.max
      : Math.floor(viewportHeight * TIMELINE_PANEL_AUTO_MAX_FRACTION_NARROW + 1e-6)
  // Never smaller than the layout default; never above the (auto) cap.
  return clampTimelinePanelHeight(
    Math.max(fallback, Math.min(preferredStackedHeightPx, autoCap)),
    bounds,
  )
}
